import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, shareReplay, tap } from 'rxjs';
import { Company, Referral } from '../models/company.model';
import { ConfigService } from '@xilo-mono/common/config';
import { Agent } from '../models';

export class ActiveUser {
  id: number;
  name: string;
  email: string;
  companyId: string;
  companyName: string;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  companyDetailsSource = new BehaviorSubject<Company>({});
  companyDetails = this.companyDetailsSource.asObservable();

  referralSourceSubject = new BehaviorSubject<any[]>([]);

  apiUrl = this.configService.apiUrl + '/company';

  constructor(private http: HttpClient, private configService: ConfigService) {}

  getCompanyDetailsById(companyId) {
    return this.http.get(`${this.configService.apiUrl}/new/client/company/${companyId}`).pipe(
      map((res: { message: string; obj: Company }) => {
        this.companyDetailsSource.next(res.obj);
        return res;
      }),
      shareReplay(1)
    );
  }

  getByUser() {
    return this.http.get(`${this.configService.apiUrl}/company/list-by-user`).pipe(
      map((res: { message: string; obj: Company }) => {
        this.companyDetailsSource.next(res.obj);
        return res;
      }),
      shareReplay(1)
    );
  }

  getBySubmissionId(submissionId) {
    return this.http.get(`${this.configService.apiUrl}/submission/${submissionId}/get-company/`).pipe(map(res => res['obj']));
  }

  getAgentById(id): Observable<Agent> {
    return this.http.get(`${this.configService.apiUrl}/agent/${id}`).pipe(
      map((result: { message: string; obj: Agent }) => {
        return result?.obj;
      }),
      shareReplay(1)
    );
  }

  getReferrals(companyId: any): Observable<Referral[]> {
    return this.http.get<Referral[]>(this.configService.apiUrl + `/referral-sources/company/${companyId}`).pipe(
      map(rs => {
        // move default to first item
        return [rs.find(r => r.name === 'Default'), ...rs.filter(r => r.name !== 'Default')];
      }),
      tap(rs => {
        this.referralSourceSubject.next(rs);
      })
    );
  }

  createBetaAccount(companyId: any, body?) {
    return this.http.post<any>(this.configService.apiUrl + `/company/${companyId}/initialize-workflows-beta`, body);
  }

  getCompany(): Observable<Company> {
    const userId = localStorage.getItem('userId');
    if (userId) {
      return this.http.get(this.apiUrl + '/profile/' + userId).pipe(map(res => res['obj']));
    } else {
      return this.http.get(this.apiUrl + '/get/agent').pipe(map(res => res['obj']));
    }
  }

  getCompanyById(companyId) {
    return this.http.get(`${this.configService.apiUrl}/new/client/company/${companyId}`).pipe(
      map((res: { message: string; obj: Company }) => res['obj']),
      shareReplay(1)
    );
  }

  getReferralsByParentId(id: number) {
    return this.http.get<Referral[]>(this.configService.apiUrl + `referral-sources/company/${id}/children`).pipe(
      map(rs => {
        // move default to first item
        return [rs.find(r => r.name === 'Default'), ...rs.filter(r => r.name !== 'Default')];
      }),
      tap(rs => {
        this.referralSourceSubject.next(rs);
      })
    );
  }

  getThemes(companyId: number) {
    return this.http.get<any>(`${this.configService.apiUrl}/themes/company/${companyId}`);
  }
}
